import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UserContextProvider, UserContextType, UserProfile } from ".";
import { ErrorBoundary2 } from "@/pages/ErrorBoundary2";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase/client";
import { jwtDecode } from "jwt-decode";
import { Database } from "@/lib/supabase/types";

export type UserChangedListener = (user: User) => void;

type PermissionsState = "idle" | "fetching" | "ready";

type UserProviderProps = { children: React.ReactNode };
export function UserProvider({ children }: UserProviderProps) {
    const sessionRef = useRef<Session>(null);
    const [user, setUser] = useState<User>(null);
    const [profile, setProfile] = useState<UserProfile>(null);
    const profileRef = useRef<UserProfile>(null);
    const profilePatchQueue = useRef<Partial<UserProfile>[]>([]);

    const [authReady, setAuthReady] = useState(false);
    const [permissionsReady, setPermissionsReady] = useState(false);

    const permissionsStateRef = useRef<PermissionsState>("idle");

    const [role, setRole] = useState<UserContextType["role"]>(null);
    const [permissions, setPermissions] = useState<UserContextType["permissions"]>(new Set());

    const updateProfile = useCallback(async (patch: Partial<UserProfile>) => {
        const prev = profileRef.current;

        if (!prev) {
            profilePatchQueue.current.push(patch);
            return true;
        }

        const next = {
            ...prev,
            ...patch,
        };


        setProfile(next);

        const { error } = await supabase.from("profiles").upsert(next);
        if (error) {
            setProfile(prev);
            console.error(error);
        }
        return !!error;
    }, []);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (permissionsStateRef.current === "fetching") return;

            permissionsStateRef.current = "fetching";
            setPermissionsReady(false);

            const { data, error } = await supabase.from("role_permissions").select("permission");
            if (error) throw error;

            setPermissions(new Set(data?.map(row => row.permission)));

            permissionsStateRef.current = "ready";
            setPermissionsReady(true);
        };

        // Set up auth state listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            setUser(session?.user ?? null);

            if (event === "INITIAL_SESSION") setAuthReady(true);

            if (session && sessionRef.current?.access_token !== session.access_token) {
                const decodedJwt = jwtDecode(session.access_token);
                setRole((decodedJwt as unknown as { user_role: Database["public"]["Enums"]["app_role"] }).user_role);

                fetchPermissions();
            }
            sessionRef.current = session;
        });

        return () => subscription.unsubscribe();
    }, [permissionsReady]);

    useEffect(() => {
        if (!profileRef.current && profile) {
            profileRef.current = profile;

            const combinedPatch: Partial<UserProfile> = {};
            Object.assign(combinedPatch, ...profilePatchQueue.current);
            updateProfile(combinedPatch);
        } else {
            profileRef.current = profile;
        }
    }, [profile, updateProfile]);

    useEffect(() => {
        if (!user?.id) {
            setProfile(null);
            return;
        }

        (async () => {
            const { data, error } = await supabase.from("profiles").select("*").eq("user_id", user.id);
            if (error) {
                console.error(error);
                return;
            }
            setProfile(data[0]);
        })();
    }, [user?.id]);

    const logout = async () => {
        try {
            await supabase.auth.refreshSession();
            await supabase.auth.signOut({ scope: "local" });

            // Unsubscribe from push subscription on logout
            const sqRegistration = await navigator.serviceWorker.ready;
            const pushSubscription = await sqRegistration.pushManager.getSubscription();
            pushSubscription.unsubscribe();
        } catch { /* empty */ }
    };

    const memoedValue = useMemo<UserContextType>(() => ({
        user,
        profile,
        role,
        permissions,
        permissionsReady,
        authReady,
        updateProfile,
        logout,
    }), [authReady, permissions, permissionsReady, profile, role, updateProfile, user]);

    return (
        <UserContextProvider value={memoedValue}>
            <ErrorBoundary2 msg="UserContext Inner">
                {children}
            </ErrorBoundary2>
        </UserContextProvider>
    );
}